import { useState, useEffect } from "react";
import { Plus, Trash2, TestTube, CheckCircle, XCircle, Loader2, Server, Eye, EyeOff, ShieldAlert, Power, KeyRound, Cloud, Activity, AlertTriangle, Network, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import SAPEndpointManager from "@/components/SAPEndpointManager";
import AdminPasswordDialog from "@/components/AdminPasswordDialog";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { isAdmin } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [formData, setFormData] = useState({ name: "", odata_url: "", username: "", password: "", system_type: "public_cloud", sap_client: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [endpointStats, setEndpointStats] = useState({});

  // Credential update state
  const [credDialogOpen, setCredDialogOpen] = useState(false);
  const [credTarget, setCredTarget] = useState(null);
  const [credForm, setCredForm] = useState({ username: "", password: "" });
  const [credSubmitting, setCredSubmitting] = useState(false);
  const [showCredPassword, setShowCredPassword] = useState(false);

  // Edit connection state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", odata_url: "", system_type: "public_cloud", sap_client: "", description: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Admin Password Auth State
  const [adminAuthModal, setAdminAuthModal] = useState({
    open: false,
    title: "",
    description: "",
    actionText: "Confirm",
    variant: "default",
    onConfirm: null,
  });

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/sap-connections`);
      setConnections(response.data);
    } catch (error) {
      console.error("Failed to fetch connections:", error);
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchConnections();
    else setLoading(false);
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="admin-required">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <ShieldAlert className="w-8 h-8 text-destructive" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
        <p className="text-muted-foreground text-center max-w-md">
          You need administrator privileges to access the settings page.
        </p>
      </div>
    );
  }

  const promptAdminAuth = ({ title, description, actionText = "Confirm", variant = "default", onConfirm }) => {
    setAdminAuthModal({ open: true, title, description, actionText, variant, onConfirm });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    promptAdminAuth({
      title: "Authorize SAP Connection Creation",
      description: `Confirm administrator password to add new SAP connection "${formData.name}".`,
      actionText: "Create Connection",
      onConfirm: async () => {
        setSubmitting(true);
        try {
          await axios.post(`${API}/sap-connections`, formData);
          toast.success("SAP connection created successfully");
          setFormData({ name: "", odata_url: "", username: "", password: "", system_type: "public_cloud", sap_client: "", description: "" });
          setDialogOpen(false);
          fetchConnections();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to create connection");
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleDelete = (id, name) => {
    promptAdminAuth({
      title: "Authorize SAP Connection Deletion",
      description: `Confirm administrator password to delete SAP connection "${name}".`,
      actionText: "Delete Connection",
      variant: "destructive",
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/sap-connections/${id}`);
          toast.success("Connection deleted");
          fetchConnections();
        } catch (error) {
          toast.error("Failed to delete connection");
        }
      }
    });
  };

  const handleTestConnection = async (id) => {
    setTestingId(id);
    setTestResults(prev => ({ ...prev, [id]: null }));
    try {
      const response = await axios.post(`${API}/sap-connections/${id}/test`);
      setTestResults(prev => ({ ...prev, [id]: response.data }));
      if (response.data.success) toast.success("Connection test successful");
      else toast.error(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || "Connection test failed";
      setTestResults(prev => ({ ...prev, [id]: { success: false, message } }));
      toast.error(message);
    } finally {
      setTestingId(null);
    }
  };

  const handleToggleActive = (id, name, isActive) => {
    promptAdminAuth({
      title: `Authorize Connection ${isActive ? "Deactivation" : "Activation"}`,
      description: `Confirm administrator password to ${isActive ? "deactivate" : "activate"} SAP system "${name}".`,
      actionText: isActive ? "Deactivate" : "Activate System",
      onConfirm: async () => {
        setTogglingId(id);
        try {
          const resp = await axios.put(`${API}/sap-connections/${id}/toggle-active`);
          toast.success(resp.data.message);
          fetchConnections();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to toggle connection");
        } finally {
          setTogglingId(null);
        }
      }
    });
  };

  const openCredDialog = (conn) => {
    setCredTarget(conn);
    setCredForm({ username: conn.username || "", password: "" });
    setShowCredPassword(false);
    setCredDialogOpen(true);
  };

  const handleCredSubmit = (e) => {
    e.preventDefault();
    if (!credForm.username && !credForm.password) {
      toast.error("Enter at least a new username or password");
      return;
    }
    promptAdminAuth({
      title: "Authorize Credentials Update",
      description: `Confirm administrator password to update SAP credentials for "${credTarget?.name}".`,
      actionText: "Update Credentials",
      onConfirm: async () => {
        setCredSubmitting(true);
        try {
          const payload = {};
          if (credForm.username) payload.username = credForm.username;
          if (credForm.password) payload.password = credForm.password;
          await axios.put(`${API}/sap-connections/${credTarget.id}/update-credentials`, payload);
          toast.success(`Credentials updated for "${credTarget.name}"`);
          setCredDialogOpen(false);
          fetchConnections();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to update credentials");
        } finally {
          setCredSubmitting(false);
        }
      }
    });
  };

  const openEditDialog = (conn) => {
    setEditTarget(conn);
    setEditForm({
      name: conn.name || "",
      odata_url: conn.odata_url || "",
      system_type: conn.system_type || "public_cloud",
      sap_client: conn.sap_client || "",
      description: conn.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      toast.error("Connection name is required");
      return;
    }
    if (!editForm.odata_url.trim()) {
      toast.error("OData URL is required");
      return;
    }
    promptAdminAuth({
      title: "Authorize Connection Update",
      description: `Confirm administrator password to update settings for SAP connection "${editTarget?.name}".`,
      actionText: "Save Changes",
      onConfirm: async () => {
        setEditSubmitting(true);
        try {
          await axios.put(`${API}/sap-connections/${editTarget.id}`, editForm);
          toast.success(`Connection "${editForm.name}" updated successfully`);
          setEditDialogOpen(false);
          fetchConnections();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to update connection");
        } finally {
          setEditSubmitting(false);
        }
      }
    });
  };

  const connectionIds = new Set(connections.map((connection) => connection.id));
  const currentEndpointStats = Object.entries(endpointStats)
    .filter(([connectionId]) => connectionIds.has(connectionId))
    .map(([, stats]) => stats);
  const uniqueEndpointKeys = new Set(
    currentEndpointStats.flatMap((stats) => stats.endpointKeys || [])
  );
  const uniqueEndpointTotal = uniqueEndpointKeys.size
    || Math.max(0, ...currentEndpointStats.map((stats) => stats.total || 0));
  const activeConnection = connections.find((connection) => connection.is_active);
  const activeEndpointStats = activeConnection
    ? endpointStats[activeConnection.id]
    : null;
  const activeHealthy = activeEndpointStats?.healthy || 0;
  const activeFailed = activeEndpointStats?.failed || 0;
  const updateEndpointStats = (connectionId, stats) => {
    setEndpointStats(prev => ({ ...prev, [connectionId]: stats }));
  };
  return (
    <div data-testid="settings-page">
      <AdminPasswordDialog
        open={adminAuthModal.open}
        onOpenChange={(open) => setAdminAuthModal(prev => ({ ...prev, open }))}
        title={adminAuthModal.title}
        description={adminAuthModal.description}
        actionText={adminAuthModal.actionText}
        variant={adminAuthModal.variant}
        onConfirm={adminAuthModal.onConfirm}
      />
      {/* Page Header */}
      <div className="page-header !mb-5">
        <div>
          <h1 className="page-title heading-display !text-3xl" data-testid="settings-title">SAP Connections</h1>
          <p className="text-muted-foreground mt-1">Manage systems, endpoints, credentials, and connectivity</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-industrial h-9" data-testid="add-connection-button">
              <Plus className="w-4 h-4 mr-2" />Add SAP System
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[500px]" data-testid="add-connection-dialog">
            <DialogHeader>
              <DialogTitle className="heading-display text-xl">New SAP Connection</DialogTitle>
              <DialogDescription>Enter your SAP OData service details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Connection Name</Label>
                <Input id="name" name="name" placeholder="Production SAP S/4HANA" value={formData.name} onChange={handleInputChange} required data-testid="input-connection-name" />
              </div>
              <div className="space-y-2">
                <Label>System Type</Label>
                <Select value={formData.system_type} onValueChange={(v) => setFormData(prev => ({ ...prev, system_type: v }))}>
                  <SelectTrigger data-testid="select-system-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public_cloud">
                      <div className="flex items-center gap-2"><Cloud className="w-4 h-4 text-blue-500" /> SAP S/4HANA Public Cloud</div>
                    </SelectItem>
                    <SelectItem value="private_cloud">
                      <div className="flex items-center gap-2"><Server className="w-4 h-4 text-violet-500" /> RISE with SAP (Private Cloud)</div>
                    </SelectItem>
                    <SelectItem value="on_premise">
                      <div className="flex items-center gap-2"><Server className="w-4 h-4 text-orange-500" /> SAP S/4HANA On-Premise</div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(formData.system_type === "private_cloud" || formData.system_type === "on_premise") && (
                <div className="space-y-2">
                  <Label htmlFor="sap_client">SAP Client</Label>
                  <Input id="sap_client" name="sap_client" placeholder="e.g. 100, 110, 300" value={formData.sap_client} onChange={handleInputChange} required data-testid="input-sap-client" />
                  <p className="text-xs text-muted-foreground">
                    {formData.system_type === "on_premise" 
                      ? "The SAP client number for your On-Premise system (e.g. 110)" 
                      : "The SAP client number for your Private Cloud system"}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="odata_url">OData Service URL</Label>
                <Input id="odata_url" name="odata_url" placeholder={
                  formData.system_type === "on_premise" 
                    ? "http://hostname:8000/sap/opu/odata/sap/API_PRODUCT_SRV" 
                    : formData.system_type === "private_cloud" 
                      ? "https://sap-server.company.com:44300/sap/opu/odata/sap/API_PRODUCT_SRV" 
                      : "https://your-tenant-api.s4hana.cloud.sap/sap/opu/odata/sap/API_PRODUCT_SRV"
                } value={formData.odata_url} onChange={handleInputChange} required data-testid="input-odata-url" />
                <p className="text-xs text-muted-foreground">
                  {formData.system_type === "on_premise"
                    ? "Enter the full URL to your On-Premise OData endpoint (HTTP or HTTPS, e.g. http://host:8000/sap/opu/odata/sap/API_PRODUCT_SRV)"
                    : formData.system_type === "private_cloud"
                      ? "Enter the full URL to your Private Cloud OData endpoint (e.g. https://host:port/sap/opu/odata/sap/API_PRODUCT_SRV)"
                      : "Enter the full URL to your SAP OData service endpoint"}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input id="description" name="description" placeholder="e.g. Production system, Development sandbox" value={formData.description} onChange={handleInputChange} data-testid="input-description" />
              </div>
              <div className="pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                    <KeyRound className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{formData.system_type === "on_premise" ? "SAP System User (SU01)" : formData.system_type === "private_cloud" ? "SAP User Credentials" : "Communication User Credentials"}</p>
                    <p className="text-xs text-muted-foreground">
                      {formData.system_type === "private_cloud"
                        ? "System/communication user created in SU01 with API authorization roles"
                        : "Technical user for API authentication"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Communication User ID</Label>
                    <Input id="username" name="username" placeholder="COMM_USER" value={formData.username} onChange={handleInputChange} required data-testid="input-username" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Communication User Password</Label>
                    <div className="relative">
                      <Input id="password" name="password" type={showPassword ? "text" : "password"} placeholder="********" value={formData.password} onChange={handleInputChange} required className="pr-10" data-testid="input-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Enter the credentials from your SAP Communication Arrangement (e.g., SAP_COM_0009 for Product API)</p>
              </div>
              <DialogFooter className="mt-6">
                <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                <Button type="submit" disabled={submitting} data-testid="submit-connection">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create Connection
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4" data-testid="sap-summary-cards">
        <Card><CardContent className="flex items-center gap-2.5 p-3"><div className="rounded-full bg-blue-50 p-2"><Server className="h-4 w-4 text-blue-600" /></div><div><p className="text-sm text-muted-foreground">SAP Systems</p><p className="text-xl font-semibold">{connections.length}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-2.5 p-3"><div className="rounded-full bg-emerald-50 p-2"><Network className="h-4 w-4 text-emerald-600" /></div><div><p className="text-sm text-muted-foreground">Active System</p><p className="text-xl font-semibold">{connections.filter(c => c.is_active).length}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-2.5 p-3"><div className="rounded-full bg-emerald-50 p-2"><Activity className="h-4 w-4 text-emerald-600" /></div><div><p className="text-sm text-muted-foreground">Healthy Endpoints</p><p className="text-xl font-semibold">{activeHealthy}/{uniqueEndpointTotal}</p><p className="text-[11px] text-muted-foreground">Active system</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-2.5 p-3"><div className="rounded-full bg-red-50 p-2"><AlertTriangle className="h-4 w-4 text-red-600" /></div><div><p className="text-sm text-muted-foreground">Failed Endpoints</p><p className="text-xl font-semibold">{activeFailed}</p><p className="text-[11px] text-muted-foreground">Active system</p></div></CardContent></Card>
      </div>

      {/* Info Banner */}
      <div className="bg-secondary/50 border border-border rounded-md px-3 py-2.5 mb-4">
        <div className="flex items-start gap-2.5">
          <Server className="w-4 h-4 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-sm font-medium">SAP S/4HANA Connections</p>
            <p className="text-sm text-muted-foreground">
              Supports <strong>SAP S/4HANA Public Cloud</strong>, <strong>RISE with SAP (Private Cloud)</strong>, and <strong>On-Premise</strong> systems. You can configure multiple connections, but only <strong>one can be active</strong> at a time. Use the power icon to switch the active connection.
            </p>
          </div>
        </div>
      </div>

      {/* Connections List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-[140px] rounded-md" />)}
        </div>
      ) : connections.length === 0 ? (
        <Card className="card-industrial" data-testid="no-connections">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Server className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No SAP Connections</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-sm">
              Add your first SAP OData connection to start fetching data from your SAP system.
            </p>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-first-connection">
              <Plus className="w-4 h-4 mr-2" />Add Your First Connection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="connections-list">
          {connections.map((connection) => {
            const isActive = connection.is_active;
            return (
              <Card
                key={connection.id}
                className={`card-industrial transition-all ${isActive ? "ring-2 ring-emerald-500/40" : "opacity-80"}`}
                data-testid={`connection-${connection.id}`}
              >
                <CardHeader className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isActive ? "bg-emerald-500/10" : "bg-secondary"}`}>
                        <Server className={`w-4 h-4 ${isActive ? "text-emerald-600" : "text-muted-foreground"}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {connection.name}
                          {isActive && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${connection.system_type === "on_premise" ? "border-orange-300 text-orange-700 bg-orange-50" : connection.system_type === "private_cloud" ? "border-violet-300 text-violet-700 bg-violet-50" : "border-blue-300 text-blue-700 bg-blue-50"}`} data-testid={`system-type-${connection.id}`}>
                            {connection.system_type === "on_premise" ? "On-Premise" : connection.system_type === "private_cloud" ? "Private Cloud" : "Public Cloud"}
                          </Badge>
                          {connection.sap_client && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Client: {connection.sap_client}</Badge>
                          )}
                        </div>
                        <CardDescription className="data-value text-xs mt-1">{connection.odata_url}</CardDescription>
                        {connection.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 italic">{connection.description}</p>
                        )}
                        {connection.username && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {connection.system_type === "on_premise" ? "SAP System User" : connection.system_type === "private_cloud" ? "SAP User" : "Communication User"}: <span className="font-medium text-foreground">{connection.username}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge
                      className={isActive
                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-100"
                      }
                      data-testid={`badge-${connection.id}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? "bg-emerald-500" : "bg-zinc-400"}`} />
                      {isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {testResults[connection.id] && (
                        testResults[connection.id].success ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                            <CheckCircle className="w-3 h-3 mr-1" />Connected
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                            <XCircle className="w-3 h-3 mr-1" />{testResults[connection.id].message}
                          </Badge>
                        )
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Toggle Active */}
                      <Button
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleToggleActive(connection.id, connection.name, isActive)}
                        disabled={togglingId === connection.id}
                        className={isActive ? "h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" : "h-8 text-xs"}
                        data-testid={`toggle-active-${connection.id}`}
                      >
                        {togglingId === connection.id
                          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <Power className="w-4 h-4 mr-1.5" />}
                        {isActive ? "Active" : "Activate"}
                      </Button>

                      {/* Change Credentials */}
                      <Button
                        variant="outline" size="sm" className="h-8 text-xs"
                        onClick={() => openCredDialog(connection)}
                        data-testid={`change-creds-${connection.id}`}
                      >
                        <KeyRound className="w-4 h-4 mr-1.5" />Credentials
                      </Button>

                      {/* Edit Details */}
                      <Button
                        variant="outline" size="sm" className="h-8 text-xs"
                        onClick={() => openEditDialog(connection)}
                        data-testid={`edit-connection-${connection.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1.5" />Edit
                      </Button>

                      {/* Test */}
                      <Button
                        variant="outline" size="sm" className="h-8 text-xs"
                        onClick={() => handleTestConnection(connection.id)}
                        disabled={!isActive || testingId === connection.id}
                        title={!isActive ? "Activate this SAP connection before testing it" : "Test SAP connection"}
                        data-testid={`test-connection-${connection.id}`}
                      >
                        {testingId === connection.id
                          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <TestTube className="w-4 h-4 mr-1.5" />}
                        Test
                      </Button>

                      {/* Delete */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(connection.id, connection.name)}
                        data-testid={`delete-connection-${connection.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <SAPEndpointManager connection={connection} onStats={updateEndpointStats} />
                  <div className="mt-2 pt-2 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Created: {new Date(connection.created_at).toLocaleDateString()}</span>
                    <span>Updated: {new Date(connection.updated_at).toLocaleDateString()}</span>
                    {connection.tenant_name && <Badge variant="secondary" className="text-xs">{connection.tenant_name}</Badge>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Change Credentials Dialog */}
      <Dialog open={credDialogOpen} onOpenChange={setCredDialogOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="update-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Update Credentials
            </DialogTitle>
            <DialogDescription>
              Update the SAP communication user credentials for connection <strong>&quot;{credTarget?.name}&quot;</strong>.
              {credTarget?.username && <> Current user: <strong>{credTarget.username}</strong>.</>}
              {" "}Leave a field empty to keep its current value.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCredSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="cred-username">SAP Communication User ID</Label>
              <Input
                id="cred-username"
                placeholder="e.g. COMM_USER"
                value={credForm.username}
                onChange={(e) => setCredForm(prev => ({ ...prev, username: e.target.value }))}
                data-testid="input-cred-username"
              />
              <p className="text-xs text-muted-foreground">The technical user from your SAP Communication Arrangement</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cred-password">SAP Communication User Password</Label>
              <div className="relative">
                <Input
                  id="cred-password"
                  type={showCredPassword ? "text" : "password"}
                  placeholder="Enter new SAP password"
                  value={credForm.password}
                  onChange={(e) => setCredForm(prev => ({ ...prev, password: e.target.value }))}
                  className="pr-10"
                  data-testid="input-cred-password"
                />
                <button type="button" onClick={() => setShowCredPassword(!showCredPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showCredPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" disabled={credSubmitting} data-testid="submit-credentials">
                {credSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Update Credentials
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Connection Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[500px]" data-testid="edit-connection-dialog">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit SAP Connection
            </DialogTitle>
            <DialogDescription>Update system details, name, description, or OData URL</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Connection Name</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="input-edit-name"
                />
              </div>
              <div className="space-y-2">
                <Label>System Type</Label>
                <Select value={editForm.system_type} onValueChange={(v) => setEditForm(prev => ({ ...prev, system_type: v }))}>
                  <SelectTrigger data-testid="select-edit-system-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public_cloud">
                      <div className="flex items-center gap-2"><Cloud className="w-4 h-4 text-blue-500" /> SAP S/4HANA Public Cloud</div>
                    </SelectItem>
                    <SelectItem value="private_cloud">
                      <div className="flex items-center gap-2"><Server className="w-4 h-4 text-violet-500" /> RISE with SAP (Private Cloud)</div>
                    </SelectItem>
                    <SelectItem value="on_premise">
                      <div className="flex items-center gap-2"><Server className="w-4 h-4 text-orange-500" /> SAP S/4HANA On-Premise</div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(editForm.system_type === "private_cloud" || editForm.system_type === "on_premise") && (
                <div className="space-y-2">
                  <Label htmlFor="edit-sap-client">SAP Client</Label>
                  <Input
                    id="edit-sap-client"
                    placeholder="e.g. 100, 110, 300"
                    value={editForm.sap_client}
                    onChange={(e) => setEditForm(prev => ({ ...prev, sap_client: e.target.value }))}
                    required
                    data-testid="input-edit-sap-client"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="edit-odata-url">OData Service URL</Label>
                <Input
                  id="edit-odata-url"
                  value={editForm.odata_url}
                  onChange={(e) => setEditForm(prev => ({ ...prev, odata_url: e.target.value }))}
                  required
                  data-testid="input-edit-odata-url"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description (optional)</Label>
                <Input
                  id="edit-description"
                  placeholder="e.g. Production system, Development sandbox"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="input-edit-description"
                />
              </div>
              <DialogFooter className="mt-6">
                <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                <Button type="submit" disabled={editSubmitting} data-testid="submit-edit-connection">
                  {editSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
